import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Activity, CheckCircle2, AlertTriangle, Clock, ShieldAlert,
  RefreshCw, RotateCw, Loader2,
} from "lucide-react";
import api from "../../lib/api";

const STATUS_STYLES = {
  ok: { label: "OK", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  partial: { label: "Parcial", className: "bg-amber-100 text-amber-800 border-amber-200" },
  shape_mismatch: { label: "Shape inválido", className: "bg-red-100 text-red-800 border-red-200" },
  invalid_json: { label: "JSON inválido", className: "bg-red-100 text-red-800 border-red-200" },
  auth_failed: { label: "Secret erróneo", className: "bg-red-100 text-red-800 border-red-200" },
  empty: { label: "Vacío", className: "bg-muted text-foreground border-border" },
  retry_ok: { label: "Retry OK", className: "bg-emerald-50 text-emerald-800 border-emerald-200 italic" },
  retry_partial: { label: "Retry parcial", className: "bg-amber-50 text-amber-800 border-amber-200 italic" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

const fmtLat = (ms) => {
  if (!ms) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

export default function WebhookHealthPanel({ restaurantId }) {
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [h, e] = await Promise.all([
        api.getWebhooksHealth(restaurantId),
        api.listWebhookEvents(restaurantId, { limit: 25 }),
      ]);
      setHealth(h);
      setEvents(e.events || []);
    } catch (err) {
      toast.error(err.message || "Error cargando salud de webhooks");
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { if (restaurantId) reload(); }, [restaurantId, reload]);

  const retry = async (eventId) => {
    setRetrying(eventId);
    try {
      const r = await api.retryWebhookEvent(restaurantId, eventId);
      toast.success(`Reintento OK · ${r.sales_processed} ventas procesadas`);
      await reload();
    } catch (err) {
      toast.error(err.message || "No se pudo reintentar");
    } finally {
      setRetrying(null);
    }
  };

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const vendors = health?.vendors || [];
  const failedEvents = (events || []).filter((e) =>
    ["shape_mismatch", "invalid_json", "auth_failed", "partial"].includes(e.status),
  );

  if (vendors.length === 0 && failedEvents.length === 0) {
    return (
      <Card data-testid="webhook-health-empty">
        <CardContent className="p-6 text-center">
          <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            Aún no hay eventos de integración. Cuando tu TPV empiece a enviar datos,
            verás aquí un resumen en vivo de su salud.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="webhook-health-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          Salud de la integración ·
          <span className="text-muted-foreground font-normal">últimas 24h</span>
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={reload}
          disabled={loading}
          data-testid="btn-refresh-health"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Vendor summary grid */}
      {vendors.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {vendors.map((v) => {
            const style = STATUS_STYLES[v.last_status] || STATUS_STYLES.ok;
            const hasErrors = v.errors_24h > 0;
            return (
              <Card key={v.vendor} data-testid={`vendor-card-${v.vendor}`}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm uppercase">{v.vendor}</span>
                    <Badge variant="outline" className={`text-[10px] ${style.className}`}>
                      {style.label}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div>
                      <div className="text-muted-foreground">Eventos</div>
                      <div className="font-semibold">{v.events_24h}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Ventas</div>
                      <div className="font-semibold text-emerald-700">{v.sales_24h}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Errores</div>
                      <div className={`font-semibold ${hasErrors ? "text-red-700" : ""}`}>
                        {v.errors_24h}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Último: {fmtDate(v.last_event_at)}</span>
                    <span className="ml-auto">{fmtLat(v.avg_latency_ms_24h)}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Recent failures */}
      {failedEvents.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="h-4 w-4 text-red-700" />
              <h4 className="text-sm font-semibold">Errores recientes</h4>
              <span className="text-[11px] text-muted-foreground ml-auto">
                {failedEvents.length} eventos
              </span>
            </div>
            <ul className="divide-y">
              {failedEvents.slice(0, 10).map((e) => {
                const style = STATUS_STYLES[e.status] || STATUS_STYLES.shape_mismatch;
                const canRetry = e.status !== "auth_failed" && e.status !== "empty";
                return (
                  <li
                    key={e.event_id}
                    className="flex items-center gap-3 py-2 text-xs"
                    data-testid={`failed-event-${e.event_id}`}
                  >
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${style.className}`}>
                      {style.label}
                    </Badge>
                    <span className="uppercase font-semibold w-16 shrink-0">{e.vendor}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-muted-foreground italic truncate">
                        {e.error_preview || "Sin detalle"}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{fmtDate(e.created_at)}</div>
                    </div>
                    {canRetry && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 shrink-0"
                        onClick={() => retry(e.event_id)}
                        disabled={retrying === e.event_id}
                        data-testid={`btn-retry-${e.event_id}`}
                      >
                        {retrying === e.event_id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <><RotateCw className="h-3 w-3 mr-1" />Reintentar</>}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {failedEvents.length === 0 && vendors.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Sin errores en las últimas 24h. Todo rodando 🚀
        </div>
      )}
    </div>
  );
}
